<style>

::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-thumb {
  background: rgba(191, 188, 188, 0.6);
}

.required_err {
  color: rgba(255, 0, 0);
}

.elevation-1 tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, .03) !important;
}

.br {
    color: rgba(109, 103, 164);
}

</style>

<template>
<div>
    <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        sort-by="map_id"
        class="elevation-1"
        @dblclick:row="on_dbl_click">

    <template v-slot:top>
        <v-toolbar flat>
            <v-toolbar-title><h2 class="br">Deterministic SNAT44 maps</h2></v-toolbar-title>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="600px">

            <template v-slot:activator="{ props }">
                <v-btn @click="new_item(item)"
                    color="primary" dark class="mb-2"
                    v-bind="props"
                    >
                    Add map
                </v-btn>
            </template>

            <ValidationObserver ref="observer" v-slot="{ invalid }"><v-card><v-form>
                <v-card-title>
                    <span class="headline">{{ edit_caption }}</span>
                </v-card-title>

                <v-card-text>
                    <v-container style="height: 520px; overflow-y: scroll;">
                        <v-row>

                    <v-col cols="12">
                        <ValidationProvider rules="required|det_nat_map_id" v-slot="{ errors }">
                            <v-text-field
                                v-bind:readonly="edited_index != -1"
                                v-model="edited_item.map_id"
                                label="Map ID"
                            ></v-text-field>
                            <span class="required_err">{{ errors[0] }}</span>
                        </ValidationProvider> 

                        <ValidationProvider rules="required|ipv4_prefix" v-slot="{ errors }">
                            <v-text-field
                                v-bind:readonly="edited_index != -1"
                                v-model="edited_item.internal_net"
                                label="Internal net"
                            >
                            </v-text-field>
                            <span class="required_err">{{ errors[0] }}</span>
                        </ValidationProvider>   

                        <ValidationProvider rules="required|ipv4_prefix" v-slot="{ errors }">
                            <v-text-field
                                v-bind:readonly="edited_index != -1"
                                v-model="edited_item.external_net"
                                label="External net"
                            >
                            </v-text-field>
                            <span class="required_err">{{ errors[0] }}</span>
                        </ValidationProvider>   

                        <ValidationProvider rules="required|numeric|powerof2" v-slot="{ errors }">
                            <v-text-field
                                v-bind:readonly="edited_index != -1"
                                v-model="edited_item.sessions_per_host"
                                label="Sessions per internal host"
                            >
                            </v-text-field>
                            <span class="required_err">{{ errors[0] }}</span>
                        </ValidationProvider>
                        
                        <v-text-field
                            v-if="edited_index !== -1"
                            v-bind:readonly="true"
                            v-model="edited_item.size"
                            label="Size"
                        ></v-text-field>                        

                        <v-text-field
                            v-if="edited_index !== -1"
                            v-bind:readonly="true"
                            v-model="edited_item.ports_per_host"
                            label="Ports per internal host"
                        ></v-text-field>

                        <div v-if="edited_index !== -1" class="br_section">
                            <h3 class="br">Statistic</h3>

                            <v-text-field                                
                                v-bind:readonly="true"
                                v-model="edited_item.active_sessions"
                                label="Active sessions"
                            ></v-text-field>

                            <v-data-table
                                v-bind:readonly="true"
                                :headers="state_counters_hdrs"
                                :items="edited_item.state_counters"
                                :search="search"
                                sort-by="state"
                                hide-default-footer
                                class="elevation-1"
                            >
                            </v-data-table>
                        </div>
                    </v-col>

                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn v-if="edited_index !== -1" color="blue darken-1"
                        text @click="close">Close</v-btn>
                    <v-btn v-if="edited_index === -1" color="blue darken-1"
                        text @click="close">Cancel</v-btn>
                    <v-btn v-if="edited_index === -1" color="blue darken-1" 
                        :disabled="invalid" text @click="save">Save</v-btn>
                </v-card-actions>
            </v-form></v-card></ValidationObserver>

            </v-dialog>
        </v-toolbar>
    
        <v-toolbar flat>
            <v-text-field v-model="search"
                append-icon="mdi-magnify" label="Search" single-line hide-details
            ></v-text-field>
        </v-toolbar>
    </template>

    <template v-slot:item.actions="{ item }">
        <v-icon small class="me-2" @click="edit_item(item)">
            mdi-pencil
        </v-icon>
        <v-icon small @click="delete_item(item)">
            mdi-delete
        </v-icon>
    </template>

    <template v-slot:no-data>
    </template>

    </v-data-table>
</div>
</template>
  
<script>

import api_client from '../services/api.js';
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { ValidationProvider, extend } from 'vee-validate';
import { ValidationObserver } from 'vee-validate';
import { required, numeric } from 'vee-validate/dist/rules';
import vif_box from "../components/vif_box.vue";
import vif_ip_addr_box from "../components/vif_ip_addr_box.vue";
import ipv4_route_table_box from "../components/ipv4_route_table_box.vue";
import "../validation_rules.js";

extend('det_nat_map_id', {
    message: 'Incorrect map id. Valid range is 0 - 4096.',
    validate: value => (value >= 0 && value <= 4096)
});

export default {
name: "det_nat",
components: { 
    Treeselect, ValidationProvider, ValidationObserver, vif_box, vif_ip_addr_box,
    ipv4_route_table_box
},
data: () => ({
    edit_caption: '',
    search: '',
    dialog: false,

    headers: [
        { text: 'Map-id', value: 'map_id', align: 'start' },
        { text: 'Internal net', value: 'internal_net' },
        { text: 'External net', value: 'external_net' },
        { text: 'Sessions', value: 'sessions_per_host' },
        { text: 'Actions', value: 'actions', sortable: false },
    ],

    state_counters_hdrs: [
        { text: 'NAT state', value: 'state', align: 'start' },
        { text: 'Counter', value: 'cnt' },
    ],

    items: [],
    edited_index: -1,
    edited_item: {
    },
    default_item: {
        id: '',
        internal_net: '',
        external_net: '',
        sessions_per_host: '',
    },
}),

watch: {
},

created() {
    this.initialize()
},

methods: {
    initialize() {
        return new Promise((resolve, reject) => {
            api_client.run_get_query('/nat/det_snat44/show')
            .then(response => {
                this.items = response.data;
                resolve(response);
            })
            .catch(error => {
                console.log(error);
            });
        });
    },

    edit_item(item) {
        this.edited_index = this.items.indexOf(item);
        this.edited_item = Object.assign({}, item);
        this.edit_caption = 'Deterministic SNAT44 map ID ' + this.edited_item.map_id;
        this.dialog = true;
    },

    new_item() {
        this.edit_caption = 'New Deterministic SNAT44 map';
        requestAnimationFrame(() => {
            this.$refs.observer.reset();
        });
        this.edited_index = -1;
        this.edited_item = Object.assign({}, this.default_item);
        this.dialog = true;
    },

    on_dbl_click(event, data) {
        this.edit_item(data.item);
    },

    close() {
        this.dialog = false;
        this.$nextTick(() => {
            this.edited_index = -1;
        });
    },

    delete_item(item) {
        this.edited_index = this.items.indexOf(item);
        this.edited_item = Object.assign({}, item);
        this.$confirm("Are you sure you want to delete Deterministic SNAT44 map " + 
            item.map_id + " ?")
        .then(() => {
            this.delete_item_confirm()
        });
    },

    delete_item_confirm() {
        api_client.run_post_query('/nat/det_snat44/del_map', {
            map_id: this.edited_item.map_id,
        })
        .then(response => {
            if (response.data.hasOwnProperty("err_msg")) {
                this.$fire({title: "Command failed", text: response.data.err_msg, type: "warning"});
            }
            else {
                this.items.splice(this.edited_index, 1);
            }
        })
        .catch(error => {
            console.log(error);
            this.$fire({title: "Command failed", text: error, type: "warning"});
        });
    },

    save() {
        api_client.run_post_query('/nat/det_snat44/add_map', {
            map_id: this.edited_item.map_id,
            internal_net: this.edited_item.internal_net,
            external_net: this.edited_item.external_net,
            sessions_per_host: this.edited_item.sessions_per_host
        })
        .then(response => {
            if (response.data.hasOwnProperty("err_msg")) {
                this.$fire({title: "Command failed", text: response.data.err_msg, type: "warning"});
            }
            else {
                this.initialize();
                this.close();
            }
        })
        .catch(error => {
            console.log(error);
            this.$fire({title: "Command failed", text: error, type: "warning"});
        });
    },   
}

};
</script>
